import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Boxes, PackageMinus, Undo2, CheckCircle2, AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { pickCatalogName, pickLocalizedField } from '@/i18n/localizeItem';
import { parseQty } from '@/lib/stockQty';
import { showApiError } from '@/lib/apiErrors';
import { useQuoteEntitlement } from '@/hooks/useQuoteEntitlement';
import { PlanLockBadge } from '@/components/quotes/PlanLockBadge';

const API_URL = process.env.REACT_APP_API_URL;
const LOCALES = { tr: 'tr-TR', en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' };


/**
 * "Materials & stock" card on an accepted quote: shows how much of the
 * quote's material has been taken out of stock and lets the user take the
 * rest out (or bring unused material back) in one step.
 */
export function QuoteMaterialsCard({ quote }) {
  const { t, i18n } = useTranslation('quotes');
  const { entitlement } = useQuoteEntitlement();
  const issueLocked = entitlement?.limits?.stock_from_quote === false;
  const lang = (i18n.language || 'tr').split('-')[0];
  const locale = LOCALES[lang] || 'tr-TR';
  const fmt = (n) => new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(Number(n) || 0);
  const name = (row) => pickCatalogName(row, lang);
  const unit = (row) => pickLocalizedField(row, 'unit', lang);

  const [data, setData] = useState(null);
  const [packageKey, setPackageKey] = useState(null);
  const [mode, setMode] = useState(null); // 'out' | 'return' | null
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async (key = packageKey) => {
    try {
      const { data: res } = await axios.get(`${API_URL}/api/quote/stock/quotes/${quote.id}/materials`, {
        params: { package_key: key || undefined },
      });
      setData(res);
      setPackageKey(res.package_key);
      return res;
    } catch (err) {
      setData({ lines: [], unlinked: [], error: true });
      return null;
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.id, quote.accepted_package_key]);

  const open = (nextMode, res = data) => {
    const initial = {};
    (res?.lines || []).forEach((l) => {
      if (nextMode === 'out' && l.remaining_qty > 0) initial[l.id] = String(l.remaining_qty);
    });
    setValues(initial);
    setMode(nextMode);
  };

  const changePackage = async (key) => {
    const res = await load(key);
    if (res && mode) open(mode, res);
  };

  const submit = async () => {
    const entries = Object.entries(values)
      .map(([item_id, raw]) => ({ item_id, qty: parseQty(raw) }))
      .filter((e) => e.qty !== null && e.qty > 0);
    if (!entries.length) {
      toast.error(t('stock.validation.qty'));
      return;
    }
    setSaving(true);
    try {
      const { data: res } = await axios.post(`${API_URL}/api/quote/stock/quotes/${quote.id}/issue`, {
        type: mode,
        entries,
        package_key: packageKey || undefined,
      });
      if (res.negative_items?.length) {
        toast.warning(t('stock.quote.negativeToast', { names: res.negative_items.join(', ') }));
      } else {
        toast.success(t(mode === 'out' ? 'stock.quote.issuedToast' : 'stock.quote.returnedToast', { count: res.moved }));
      }
      setMode(null);
      load();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  if (!data || data.error) return null;
  const lines = data.lines || [];
  const unlinked = data.unlinked || [];
  if (!lines.length && !unlinked.length) return null;

  const anyIssued = lines.some((l) => l.issued_qty > 0);
  const packages = quote.packages || [];
  const canPickPackage = !data.package_locked && packages.length > 1;

  return (
    <div className="mb-6 rounded-2xl border border-[#E5E5E5] bg-white p-4" data-testid="quote-materials-card">
      <div className="flex flex-wrap items-center gap-3">
        <Boxes className="h-5 w-5 text-[#2563EB]" />
        <h2 className="font-semibold">{t('stock.quote.title')}</h2>
        {data.fully_issued ? (
          <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">
            <CheckCircle2 className="h-3.5 w-3.5" /> {t('stock.quote.fullyIssued')}
          </span>
        ) : anyIssued ? (
          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">{t('stock.quote.partlyIssued')}</span>
        ) : null}
        <div className="ml-auto flex flex-wrap gap-2">
          {anyIssued && (
            <Button variant="outline" size="sm" onClick={() => open('return')} data-testid="quote-materials-return">
              <Undo2 className="mr-1.5 h-4 w-4" /> {t('stock.quote.returnButton')}
            </Button>
          )}
          {lines.length > 0 && !data.fully_issued && (
            <Button size="sm" onClick={() => (issueLocked ? window.location.assign('/quote-upgrade') : open('out'))} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="quote-materials-issue">
              <PackageMinus className="mr-1.5 h-4 w-4" /> {t('stock.quote.issueButton')}
              {issueLocked && <PlanLockBadge />}
            </Button>
          )}
        </div>
      </div>

      {lines.length > 0 && (
        <ul className="mt-3 divide-y divide-[#F1F5F9] text-sm">
          {lines.map((l) => (
            <li key={l.id} className="flex items-center gap-3 py-2">
              <span className="min-w-0 flex-1 truncate">{name(l)}</span>
              <span className="shrink-0 text-xs text-[#595959]">
                {t('stock.quote.progress', { issued: fmt(l.issued_qty), planned: fmt(l.planned_qty), unit: unit(l) })}
              </span>
            </li>
          ))}
        </ul>
      )}
      {unlinked.length > 0 && (
        <p className="mt-3 flex gap-2 rounded-lg bg-slate-50 p-2.5 text-xs text-slate-600">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          <span>{t('stock.quote.unlinked', { names: unlinked.map((u) => u.name).join(', ') })}</span>
        </p>
      )}

      <Dialog open={!!mode} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t(mode === 'return' ? 'stock.quote.returnTitle' : 'stock.quote.issueTitle')}</DialogTitle>
            <DialogDescription>
              {[quote.quote_number, quote.title].filter(Boolean).join(' · ')}
              {' — '}
              {t(mode === 'return' ? 'stock.quote.returnHelp' : 'stock.quote.issueHelp')}
            </DialogDescription>
          </DialogHeader>

          {canPickPackage && mode === 'out' && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-[#475569]">{t('stock.quote.package')}:</span>
              <select
                value={packageKey || ''}
                onChange={(e) => changePackage(e.target.value)}
                className="rounded-md border border-slate-200 bg-white px-2 py-1.5"
                data-testid="quote-materials-package"
              >
                {packages.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
              </select>
            </div>
          )}

          <ul className="max-h-[50vh] divide-y divide-[#F1F5F9] overflow-y-auto">
            {lines
              .filter((l) => (mode === 'return' ? l.issued_qty > 0 : true))
              .map((l) => (
                <li key={l.id} className="flex items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-[#1A1A2E]">{name(l)}</div>
                    <div className={`text-xs ${l.stock_qty < 0 ? 'text-red-600' : 'text-[#94A3B8]'}`}>
                      {mode === 'return'
                        ? t('stock.quote.issuedLine', { value: fmt(l.issued_qty), unit: unit(l) })
                        : t('stock.quote.lineInfo', {
                          planned: fmt(l.planned_qty), stock: fmt(l.stock_qty), unit: unit(l),
                        })}
                    </div>
                  </div>
                  <Input
                    inputMode="decimal"
                    className="w-24 text-right"
                    placeholder="0"
                    value={values[l.id] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [l.id]: e.target.value }))}
                    data-testid={`quote-materials-input-${l.id}`}
                  />
                </li>
              ))}
          </ul>
          {mode === 'out' && (
            <p className="text-xs text-[#94A3B8]">
              {t('stock.quote.editHint')} <Link to="/quote-stock" className="text-[#2563EB] underline">{t('stock.quote.openStock')}</Link>
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setMode(null)}>{t('common.cancel')}</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="quote-materials-save">
              {t(mode === 'return' ? 'stock.quote.returnSave' : 'stock.quote.issueSave')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
